import { Autocomplete, MenuItem, Select, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers-pro";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import CommonTextField from "../../common/components/functional/commonTextField/CommonTextField";
import { TALENTS_PAGE_SIZE } from "../../common/components/functional/pagination/constants";
import { SelectAutocompleteMulti } from "../../common/components/functional/SelectAutocomplete/SelectAutocompleteMulti";
import { SelectAutocompleteSingle } from "../../common/components/functional/SelectAutocomplete/SelectAutocompleteSingle";
import { SALARY_TYPE_OPTIONS } from "../../common/constants/SalaryTypes";
import { trueFalseAutocompleteOptions } from "../../common/helpers/common";
import { leadingZerosDateFormat } from "../../common/helpers/formatDateTime";
import { FilterRow } from "../invoices/filters/FilterRow";
import { EezyCompany } from "../invoices/invoiceAPI";
import { fetchEezyCompanies } from "../invoices/invoiceSlice";
import { SCHEDULING_PERIOD_TYPES } from "../settings/helpers/utils";
import { Talent } from "../talents/talentAPI";
import { fetchTalentsAutocomplete } from "../talents/talentsSlice";
import {
  PAYSLIP_STATUS,
  TYPE_PAYSLIP,
  TYPE_WAGE_REPORT,
  WAGE_REPORT_STATUS,
} from "./constants";
import { SalaryFilterFormTypes } from "./filters/common";

const datePickerSlotProps = {
  textField: { size: "small", label: null },
} as const;

export const SalaryFilters = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { control, watch } = useFormContext<SalaryFilterFormTypes>();

  const type = watch("type");

  const [eezyCompanyOptions, setEezyCompanyOptions] = useState<EezyCompany[]>(
    [],
  );
  const [displayTalents, setDisplayTalents] = useState<Talent[]>([]);
  const [talentSearchString, setTalentSearchString] = useState<string>("");

  useEffect(() => {
    if (talentSearchString.length > 2) {
      dispatch(
        fetchTalentsAutocomplete({
          search: talentSearchString,
          pageSize: TALENTS_PAGE_SIZE,
        }),
      ).then((action) => {
        if (isFulfilled(action)) {
          setDisplayTalents(action.payload.data);
        }
      });
    }
  }, [talentSearchString, dispatch]);

  useEffect(() => {
    dispatch(fetchEezyCompanies({})).then(
      (action) => isFulfilled(action) && setEezyCompanyOptions(action.payload),
    );
  }, [dispatch]);

  const employerOptions = useMemo(
    () =>
      eezyCompanyOptions.map((company) => ({
        id: company.eezyCompanyId,
        name: company.name,
      })),
    [eezyCompanyOptions],
  );

  // Option names are stored in the filter chip's lookupValues, so they are translated here
  const payslipStatusOptions = useMemo(
    () =>
      PAYSLIP_STATUS.map((status) => ({
        id: status.value,
        name: t(`payslips:${status.name}`),
      })),
    [t],
  );
  const salaryTypeOptions = useMemo(
    () =>
      SALARY_TYPE_OPTIONS.map((salaryType) => ({
        id: salaryType.id,
        name: t(salaryType.name),
      })),
    [t],
  );

  if (!type) return null;

  const isPayslip = type === TYPE_PAYSLIP;

  return (
    <>
      <FilterRow label={t("payslips:salaryDate")}>
        <Controller
          name="salaryDateFrom"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DesktopDatePicker
              format={leadingZerosDateFormat}
              value={value}
              onChange={onChange}
              slotProps={datePickerSlotProps}
            />
          )}
        />
        <Typography>-</Typography>
        <Controller
          name="salaryDateTo"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DesktopDatePicker
              format={leadingZerosDateFormat}
              value={value}
              onChange={onChange}
              slotProps={datePickerSlotProps}
            />
          )}
        />
      </FilterRow>
      {isPayslip ? (
        <FilterRow label={t("payslips:valueDate")}>
          <Controller
            name="valueDateFrom"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DesktopDatePicker
                format={leadingZerosDateFormat}
                value={value}
                onChange={onChange}
                slotProps={datePickerSlotProps}
              />
            )}
          />
          <Typography>-</Typography>
          <Controller
            name="valueDateTo"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DesktopDatePicker
                format={leadingZerosDateFormat}
                value={value}
                onChange={onChange}
                slotProps={datePickerSlotProps}
              />
            )}
          />
        </FilterRow>
      ) : null}
      {isPayslip ? (
        <FilterRow label={t("payslips:netSalary")}>
          <Controller
            name="incomeNetFrom"
            control={control}
            render={({ field: { value, onChange } }) => (
              <CommonTextField
                size="small"
                type="number"
                sx={{ width: "42%" }}
                value={value}
                onChange={onChange}
              />
            )}
          />
          <Typography sx={{ paddingLeft: 2, paddingRight: 2 }}>
            {"-"}
          </Typography>
          <Controller
            name="incomeNetTo"
            control={control}
            render={({ field: { value, onChange } }) => (
              <CommonTextField
                size="small"
                type="number"
                sx={{ width: "42%" }}
                value={value}
                onChange={onChange}
              />
            )}
          />
        </FilterRow>
      ) : null}
      {isPayslip ? (
        <FilterRow label={t("payslips:salaryType")}>
          <Controller
            name="salaryTypes"
            control={control}
            render={({ field: { value, onChange } }) => (
              <SelectAutocompleteMulti
                value={value}
                onChange={(newValue) => onChange(newValue || [])}
                options={salaryTypeOptions}
                translationNamespaces={["payslips"]}
              />
            )}
          />
        </FilterRow>
      ) : null}
      <FilterRow label={t("employer")}>
        <Controller
          name="eezyCompanies"
          control={control}
          render={({ field: { value, onChange } }) => (
            <SelectAutocompleteMulti
              value={value}
              onChange={(newValue) => onChange(newValue || [])}
              options={employerOptions}
            />
          )}
        />
      </FilterRow>
      <FilterRow label={t("payslips:talent")}>
        <Controller
          name="talent"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Autocomplete
              fullWidth
              value={value}
              sx={{ backgroundColor: "rgba(249, 249, 250, 0.6)" }}
              getOptionLabel={(option) =>
                `${option?.account?.firstName} ${option?.account?.lastName}`
              }
              options={displayTalents}
              renderInput={(params) => (
                <CommonTextField {...params} size="small" name="talent" />
              )}
              onInputChange={(_, newValue: string) => {
                if (newValue) {
                  setTalentSearchString(newValue);
                }
              }}
              onChange={(_, newValue) => onChange(newValue)}
              noOptionsText={t("common:writeToSearch")}
            />
          )}
        />
      </FilterRow>
      <FilterRow label={t("payslips:ssn")}>
        <Controller
          name="ssn"
          control={control}
          render={({ field: { value, onChange } }) => (
            <CommonTextField
              value={value}
              size="small"
              fullWidth
              onChange={onChange}
              autoComplete="off"
            />
          )}
        />
      </FilterRow>
      <FilterRow label={t("payslips:salaryAndBillingPeriod")}>
        <Controller
          name="salaryPeriodClass"
          control={control}
          render={({ field: { value, onChange } }) => (
            <SelectAutocompleteSingle
              value={value}
              onChange={onChange}
              options={SCHEDULING_PERIOD_TYPES}
              translationNamespaces={["schedulingPeriods"]}
            />
          )}
        />
      </FilterRow>
      {type === TYPE_WAGE_REPORT ? (
        <FilterRow label={t("payslips:statues")}>
          <Controller
            name="statues"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                fullWidth
                size="small"
                value={value}
                label={null}
                onChange={onChange}
              >
                {WAGE_REPORT_STATUS.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {t(`payslips:${status.name}`)}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FilterRow>
      ) : null}
      {isPayslip ? (
        <>
          <FilterRow label={t("payslips:payslipStatus")}>
            <Controller
              name="statusCodes"
              control={control}
              render={({ field: { value, onChange } }) => (
                <SelectAutocompleteMulti
                  value={value}
                  onChange={(newValue) => onChange(newValue || [])}
                  options={payslipStatusOptions}
                  translationNamespaces={["payslips"]}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("payslips:manualPayslip")}>
            <Controller
              name="isManual"
              control={control}
              render={({ field: { value, onChange } }) => (
                <SelectAutocompleteSingle
                  value={value}
                  onChange={onChange}
                  options={trueFalseAutocompleteOptions}
                />
              )}
            />
          </FilterRow>
        </>
      ) : null}
    </>
  );
};
